import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Button, Card, Form, Input, Modal, Space, Table, Tabs, Tag, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { sellerRolesAPI } from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface PermissionOption {
  name: string
  display_name: string
  description?: string | null
}

interface RolePermission {
  id: number
  name: string
  display_name: string
}

interface Role {
  id: number
  name: string
  display_name: string
  description?: string | null
  permissions: RolePermission[]
  users_count: number
}

interface RoleFormValues {
  display_name: string
  description?: string
  permissions: string[]
}

interface PermissionMatrixRow {
  domain: string
  domainLabel: string
  permissions: PermissionOption[]
}

const actionOrder = ['create', 'read', 'update', 'delete', 'reply', 'close', 'request']

const toTitle = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const buildPermissionMatrix = (permissionList: PermissionOption[]): PermissionMatrixRow[] => {
  const grouped = new Map<string, PermissionOption[]>()

  permissionList.forEach((permission) => {
    const [domain] = permission.name.split('.')
    const key = domain || 'general'
    const current = grouped.get(key) ?? []
    current.push(permission)
    grouped.set(key, current)
  })

  return [...grouped.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([domain, domainPermissions]) => ({
      domain,
      domainLabel: toTitle(domain),
      permissions: [...domainPermissions].sort((left, right) => {
        const [, leftAction = 'read'] = left.name.split('.')
        const [, rightAction = 'read'] = right.name.split('.')
        const leftIndex = actionOrder.indexOf(leftAction)
        const rightIndex = actionOrder.indexOf(rightAction)

        if (leftIndex === -1 && rightIndex === -1) {
          return leftAction.localeCompare(rightAction)
        }

        if (leftIndex === -1) {
          return 1
        }

        if (rightIndex === -1) {
          return -1
        }

        return leftIndex - rightIndex
      }),
    }))
}

const RolesPage = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<PermissionOption[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [form] = Form.useForm<RoleFormValues>()
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canCreateRole = hasPermission('roles.create')
  const canUpdateRole = hasPermission('roles.update')
  const canDeleteRole = hasPermission('roles.delete')

  const permissionMatrix = useMemo(() => buildPermissionMatrix(permissions), [permissions])

  const fetchRoles = useCallback(async () => {
    setLoading(true)
    try {
      const response = await sellerRolesAPI.list()
      setRoles(response.data.data.data ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load roles')
    } finally {
      setLoading(false)
    }
  }, [message])

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await sellerRolesAPI.permissions()
      setPermissions(response.data.data ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load permissions')
    }
  }, [message])

  useEffect(() => {
    void Promise.all([fetchRoles(), fetchPermissions()])
  }, [fetchPermissions, fetchRoles])

  const openCreate = () => {
    if (!canCreateRole) {
      message.warning('You are not permitted to create roles.')
      return
    }

    setEditingRole(null)
    form.resetFields()
    setSelectedPermissions([])
    setModalVisible(true)
  }

  const openEdit = (role: Role) => {
    if (!canUpdateRole) {
      message.warning('You are not permitted to update roles.')
      return
    }

    setEditingRole(role)
    form.setFieldsValue({
      display_name: role.display_name,
      description: role.description ?? '',
      permissions: role.permissions?.map((permission) => permission.name) ?? [],
    })
    setSelectedPermissions(role.permissions?.map((permission) => permission.name) ?? [])
    setModalVisible(true)
  }

  const closeModal = () => {
    setEditingRole(null)
    setModalVisible(false)
    setSelectedPermissions([])
    form.resetFields()
  }

  const submit = async (values: RoleFormValues) => {
    if (editingRole && !canUpdateRole) {
      message.warning('You are not permitted to update roles.')
      return
    }

    if (!editingRole && !canCreateRole) {
      message.warning('You are not permitted to create roles.')
      return
    }

    setSaving(true)
    try {
      if (editingRole) {
        await sellerRolesAPI.update(editingRole.id, values)
        message.success('Role updated')
      } else {
        await sellerRolesAPI.create(values)
        message.success('Role created')
      }
      closeModal()
      await fetchRoles()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save role')
    } finally {
      setSaving(false)
    }
  }

  const deleteRole = async (id: number) => {
    if (!canDeleteRole) {
      message.warning('You are not permitted to delete roles.')
      return
    }

    try {
      await sellerRolesAPI.delete(id)
      message.success('Role deleted')
      await fetchRoles()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete role')
    }
  }

  const columns: ColumnsType<Role> = [
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.display_name}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.name}</div>
        </div>
      ),
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => (
        <Space wrap>
          {(record.permissions ?? []).slice(0, 4).map((permission) => (
            <Tag key={permission.id}>{permission.display_name}</Tag>
          ))}
          {(record.permissions?.length ?? 0) > 4 ? <Tag>+{(record.permissions?.length ?? 0) - 4} more</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Users',
      dataIndex: 'users_count',
      key: 'users_count',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canUpdateRole ? (
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)}>
              Edit
            </Button>
          ) : null}
          {canDeleteRole ? (
            <Button danger icon={<DeleteOutlined />} onClick={() => void deleteRole(record.id)}>
              Delete
            </Button>
          ) : null}
          {!canUpdateRole && !canDeleteRole ? <Tag color="default">Read only</Tag> : null}
        </Space>
      ),
    },
  ]

  const togglePermission = (permissionName: string, checked: boolean) => {
    const next = new Set(selectedPermissions)

    if (checked) {
      next.add(permissionName)
    } else {
      next.delete(permissionName)
    }

    const nextValues = [...next]
    form.setFieldValue('permissions', nextValues)
    setSelectedPermissions(nextValues)
  }

  const toggleDomain = (domainPermissions: PermissionOption[], checked: boolean) => {
    const next = new Set(selectedPermissions)

    domainPermissions.forEach((permission) => {
      if (checked) {
        next.add(permission.name)
      } else {
        next.delete(permission.name)
      }
    })

    const nextValues = [...next]
    form.setFieldValue('permissions', nextValues)
    setSelectedPermissions(nextValues)
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Roles & Permissions</h1>
        <div style={{ color: '#64748b', marginTop: 6 }}>
          Configure seller team roles and choose what each role can create, read, update, or delete.
        </div>
      </div>

      <Tabs
        defaultActiveKey="roles"
        items={[
          {
            key: 'roles',
            label: 'Role Management',
            children: (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  {canCreateRole ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                      Create Role
                    </Button>
                  ) : null}
                </div>

                <Table columns={columns} dataSource={roles} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 860 }} />
              </>
            ),
          },
          {
            key: 'permissions',
            label: 'Permission Catalog',
            children: (
              <div style={{ display: 'grid', gap: 14 }}>
                {permissionMatrix.map((row) => (
                  <Card key={row.domain} size="small">
                    <Typography.Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                      {row.domainLabel}
                    </Typography.Title>
                    <Space wrap>
                      {row.permissions.map((permission) => (
                        <Tag key={permission.name} color="blue">
                          {permission.display_name}
                        </Tag>
                      ))}
                    </Space>
                  </Card>
                ))}
              </div>
            ),
          },
        ]}
      />

      <Modal title={editingRole ? 'Edit Role' : 'Create Role'} open={modalVisible} onCancel={closeModal} footer={null}>
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="display_name" label="Display Name" rules={[{ required: true, message: 'Role name is required' }]}>
            <Input placeholder="Inventory Manager" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Describe this role" />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions" rules={[{ required: true, message: 'Pick at least one permission' }]}>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
              {permissionMatrix.map((row) => {
                const selectedInDomain = row.permissions.filter((permission) => selectedPermissions.includes(permission.name)).length
                const allSelected = selectedInDomain === row.permissions.length && row.permissions.length > 0

                return (
                  <div key={row.domain} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8faf9' }}>
                      <div>
                        <strong>{row.domainLabel}</strong>
                        <div style={{ color: '#64748b', fontSize: 12 }}>{selectedInDomain} of {row.permissions.length} selected</div>
                      </div>
                      <Space size={6}>
                        <Button size="small" onClick={() => toggleDomain(row.permissions, true)}>
                          All
                        </Button>
                        <Button size="small" onClick={() => toggleDomain(row.permissions, false)} disabled={!allSelected && selectedInDomain === 0}>
                          Clear
                        </Button>
                      </Space>
                    </div>
                    <div style={{ padding: '10px 14px' }}>
                      <Space wrap>
                        {row.permissions.map((permission) => {
                          const checked = selectedPermissions.includes(permission.name)
                          const [, action = permission.name] = permission.name.split('.')

                          return (
                            <Button
                              key={permission.name}
                              type={checked ? 'primary' : 'default'}
                              size="small"
                              onClick={() => togglePermission(permission.name, !checked)}
                            >
                              {toTitle(action)}
                            </Button>
                          )
                        })}
                      </Space>
                    </div>
                  </div>
                )
              })}
            </div>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default RolesPage
